/**
 * Modern Sidebar Component
 * Collapsible navigation with icons and smooth transitions
 */

import React, { useState } from 'react';
import { Box, Drawer, List, Typography, IconButton, Divider, Avatar, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import SidebarNavItem from './SidebarNavItem';
import { setShowList } from '../../../redux/modules/showList/slice';
import { USER_ROLE_DEFAULT } from '../../../constants/Constant';
import { isFeatureEnabled } from '../../../config/features';
import features from '../../../config/features';
import { hasModuleAccess, can as canCapability } from '../../../utils/permissions';

// Sidebar widths
const DRAWER_WIDTH_EXPANDED = 260;
const DRAWER_WIDTH_COLLAPSED = 72;

// Styled components
const DrawerHeader = styled(Box)(({ theme, collapsed }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: collapsed ? 'center' : 'space-between',
  padding: collapsed ? theme.spacing(2) : theme.spacing(2, 3),
  minHeight: 64,
}));

const LogoContainer = styled(Box)(({ theme, collapsed }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  overflow: 'hidden',
  transition: 'all 200ms ease',
}));

const DrawerFooter = styled(Box)(({ theme, collapsed }) => ({
  marginTop: 'auto',
  padding: collapsed ? theme.spacing(2, 1) : theme.spacing(2, 2),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const UserSection = styled(Box)(({ theme, collapsed }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: collapsed ? theme.spacing(1) : theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  cursor: 'pointer',
  transition: 'all 150ms ease',
  justifyContent: collapsed ? 'center' : 'flex-start',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StyledDrawer = styled(Drawer)(({ theme, collapsed }) => ({
  width: collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  '& .MuiDrawer-paper': {
    width: collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED,
    boxSizing: 'border-box',
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    transition: 'width 200ms ease',
    overflowX: 'hidden',
  },
}));

// Navigation items configuration
const getNavItems = (basePath, userData, permissions) => {
  const role = userData?.role;
  const can = (moduleKey) => hasModuleAccess(permissions, role, moduleKey);
  // The Settings area is visible if the user can reach any of its sub-pages:
  // Branches, Audit Log, or (admin only) Role Permissions.
  const canSettings = role === 'admin' || permissions?.isSuperAdmin
    || canCapability(permissions, 'branches:write')
    || canCapability(permissions, 'auditLogs:read');

  const items = [
    {
      to: basePath,
      label: 'Dashboard',
      icon: <DashboardOutlinedIcon />,
      exact: true,
      visible: can('dashboard'),
    },
    {
      to: `${basePath}/patient-management`,
      label: 'Patients',
      icon: <PeopleOutlinedIcon />,
      visible: can('patients') && isFeatureEnabled('core.patientManagement'),
    },
    {
      to: `${basePath}/encounters`,
      label: 'Encounters',
      icon: <EventNoteOutlinedIcon />,
      visible: can('encounters'),
    },
    {
      to: `${basePath}/users`,
      label: 'Users',
      icon: <PersonOutlinedIcon />,
      visible: can('users'),
    },
    {
      to: `${basePath}/settings`,
      label: 'Settings',
      icon: <SettingsOutlinedIcon />,
      visible: canSettings,
    },
  ];

  return items.filter(item => item.visible);
};

const Sidebar = ({
  basePath = '/admin',
  isCollapsed = false,
  mobileOpen = false,
  onToggleCollapse,
  onCloseMobile,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userData } = useSelector(state => state.auth);
  const { myPermissions, roles: allRoles } = useSelector(state => state.permissions);
  const navItems = getNavItems(basePath, userData, myPermissions);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const handleNavClick = () => {
    dispatch(setShowList(true));
    if (mobileOpen) {
      onCloseMobile?.();
    }
  };

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = () => {
    setLogoutDialogOpen(false);
    localStorage.clear();
    navigate('/login');
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  const getUserInitials = () => {
    if (userData?.name) {
      return userData.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return 'U';
  };

  // Drawer content
  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header with Logo */}
      <DrawerHeader collapsed={isCollapsed ? 1 : 0}>
        <LogoContainer collapsed={isCollapsed ? 1 : 0}>
          <Box
            component="img"
            src={require('../../../assets/images/dwms-logo-navbar.png')}
            alt="DWMS Logo"
            sx={{
              height: 36,
              width: 'auto',
              objectFit: 'contain',
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          {!isCollapsed && (
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: 'primary.dark', lineHeight: 1.2 }}
              >
                {features.client?.shortName || 'PACS'}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontSize: '0.7rem' }}
              >
                Medical Imaging
              </Typography>
            </Box>
          )}
        </LogoContainer>
        {!isCollapsed && onToggleCollapse && (
          <IconButton
            onClick={onToggleCollapse}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'primary.main' },
            }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        )}
      </DrawerHeader>

      {/* Collapsed toggle button */}
      {isCollapsed && onToggleCollapse && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <IconButton
            onClick={onToggleCollapse}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'primary.main' },
            }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Divider />

      {/* Navigation Items */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 2, px: isCollapsed ? 1 : 2 }}>
        <List disablePadding>
          {navItems.map((item) => (
            <SidebarNavItem
              key={item.to}
              to={item.to}
              label={item.label}
              icon={item.icon}
              collapsed={isCollapsed}
              exact={item.exact}
              onClick={handleNavClick}
            />
          ))}
        </List>
      </Box>

      {/* Footer with User */}
      <DrawerFooter collapsed={isCollapsed ? 1 : 0}>
        <Tooltip title={isCollapsed ? 'Logout' : ''} placement="right">
          <UserSection
            collapsed={isCollapsed ? 1 : 0}
            onClick={handleLogoutClick}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: 'primary.main',
                fontSize: '0.875rem',
              }}
            >
              {getUserInitials()}
            </Avatar>
            {!isCollapsed && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {userData?.name || 'User'}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary' }}
                >
                  {(allRoles || []).find(r => r.role === userData?.role)?.displayText || userData?.role?.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim() || 'Staff'}
                </Typography>
              </Box>
            )}
            {!isCollapsed && (
              <LogoutOutlinedIcon
                sx={{ color: 'text.secondary', fontSize: 20 }}
              />
            )}
          </UserSection>
        </Tooltip>
      </DrawerFooter>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: 320,
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Confirm Logout</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to log out of your account?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleLogoutCancel}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogoutConfirm}
            variant="contained"
            color="error"
            sx={{ borderRadius: 2 }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { xs: 0, lg: isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED },
        flexShrink: 0,
        transition: 'width 200ms ease',
      }}
    >
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onCloseMobile}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH_EXPANDED,
            boxSizing: 'border-box',
            backgroundColor: 'background.paper',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer */}
      <StyledDrawer
        variant="permanent"
        collapsed={isCollapsed ? 1 : 0}
        sx={{
          display: { xs: 'none', lg: 'block' },
        }}
      >
        {drawerContent}
      </StyledDrawer>
    </Box>
  );
};

export { DRAWER_WIDTH_EXPANDED, DRAWER_WIDTH_COLLAPSED };
export default Sidebar;
